import { useRestorePlayMutation } from '@carboncopyco/data';
import { RevertOutlineIcon, ShowOutlineIcon } from '@carboncopyco/icons';
import type { Play } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Loading,
  TextTooltip,
  Tooltip,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { isDesktop, usePlaybookAnalytics } from '@carboncopyco/utils';
import { useRef, useState } from 'react';
import { PlaybookPreviewTooltip } from '../../../Tooltip';
import { usePlaybookPageProps } from '../PlaybookPageContext';
import { usePresentationsContext } from '../PresentationsContext';

interface ArchivedRowButtonsProps {
  presentation: Play;
}

export const ArchivedRowButtons = ({
  presentation,
}: ArchivedRowButtonsProps) => {
  const { trackPlaybookClickPreview } = usePlaybookAnalytics(
    'archived_presentations_tab'
  );
  const { organizationId, goToPlaybookPreview, desktopDomain } =
    usePlaybookPageProps();
  const [restorePresentation] = useRestorePlayMutation();
  const { removeItem: removePresentation } = usePresentationsContext();
  const previewRef = useRef<HTMLDivElement>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const handlePreview = async () => {
    try {
      setIsPreviewLoading(true);
      trackPlaybookClickPreview({
        playbookId: presentation.asset.assetId,
      });
      await goToPlaybookPreview(presentation);
    } catch (_error) {
      notify.error('Error previewing playbook');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div ref={previewRef}>
        <Button
          dark
          disabled={!isDesktop() || isPreviewLoading}
          icon={
            isPreviewLoading ? (
              <Loading />
            ) : (
              <ShowOutlineIcon
                color={!isDesktop() ? ColorHex.natural200 : ColorHex.natural600}
              />
            )
          }
          onClick={handlePreview}
        />
      </div>

      {isDesktop() ? (
        <Tooltip
          background="dark"
          anchorRef={previewRef}
          placement="top"
          noArrow
        >
          <div className="px-1 pb-1 pt-0 leading-none break-words">
            <TextTooltip
              label="Preview"
              color="text-ds-natural-50"
            />
          </div>
        </Tooltip>
      ) : (
        <PlaybookPreviewTooltip
          desktopDomain={desktopDomain}
          previewRef={previewRef}
          location={location}
          playId={presentation.id}
        />
      )}
      <TooltipBubble
        placement="top"
        title="Restore"
      >
        <Button
          aria-label="restore"
          dark
          icon={<RevertOutlineIcon color={ColorHex.natural600} />}
          onClick={() => {
            restorePresentation({
              organizationId,
              playId: String(presentation.id),
            })
              .unwrap()
              .then(() => {
                removePresentation(String(presentation.id));
                notify.success('Presentation was successfully restored');
              })
              .catch(() => notify.error('Error restoring presentation'));
          }}
        />
      </TooltipBubble>
    </div>
  );
};
